import {
  APP_VERSION,
  type GameCommand,
  type PlayerSessionView,
  type RoomSection,
  type RoomView,
} from "@secret-cabinet/contracts";
import { ArrowLeft, CircleAlert, LoaderCircle, RotateCcw } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useState } from "react";
import { PhaseBanner } from "./components/PhaseBanner";
import { PlayerRail } from "./components/PlayerRail";
import { PrivateDocket } from "./components/PrivateDocket";
import { PublicBoard } from "./components/PublicBoard";
import { RoomHeader } from "./components/RoomHeader";
import { HistoryPanel, QrPanel, RulesPanel } from "./components/RoomPanels";
import { RoomNav } from "./components/RoomNav";
import { useRoomSocket, type ConnectionStatus } from "./use-room-socket";
import { useGameAudio } from "./audio";

interface RoomProps {
  code: string;
  mode: "player" | "tv";
  session: PlayerSessionView | null;
  onExit: () => void;
  onLeave: () => void;
  onForgetSession: () => void;
}

export function Room({ code, mode, session, onExit, onLeave, onForgetSession }: RoomProps) {
  const {
    view,
    status,
    error,
    retryConnection,
    sendCommand,
    afkWarningMs,
    presenceRequired,
    confirmPresence,
  } = useRoomSocket(code, mode, session);
  const [section, setSection] = useState<RoomSection>("chamber");
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setActionError(null);
    setPending(false);
  }, [view?.public.phase]);

  async function dispatch(command: GameCommand): Promise<void> {
    setPending(true);
    setActionError(null);
    const result = await sendCommand(command);
    if (!result.ok) {
      setActionError(result.message);
    }
    setPending(false);
  }

  if (!view) {
    return (
      <LoadingRoom
        code={code}
        mode={mode}
        status={status}
        error={error}
        onRetry={retryConnection}
        onExit={onExit}
        onLeave={onLeave}
        onForgetSession={onForgetSession}
      />
    );
  }

  return mode === "tv" ? (
      <TvRoom view={view} status={status} error={error} onRetry={retryConnection} onExit={onExit} />
  ) : (
    <PhoneRoom
      view={view}
      status={status}
      section={section}
      setSection={setSection}
      pending={pending}
      actionError={actionError ?? error?.message ?? null}
      dispatch={dispatch}
      error={error}
      onRetry={retryConnection}
      onForgetSession={onForgetSession}
      onLeave={onLeave}
      afkWarningMs={afkWarningMs}
      presenceRequired={presenceRequired}
      confirmPresence={confirmPresence}
    />
  );
}

export function StaticRoomPreview({
  view,
  mode,
  initialSection = "chamber",
  status = "live",
  error = null,
  actionError = null,
  pending = false,
  afkWarningMs = null,
  presenceRequired = false,
}: {
  view: RoomView | null;
  mode: "player" | "tv";
  initialSection?: RoomSection;
  status?: ConnectionStatus;
  error?: { code: string; message: string } | null;
  actionError?: string | null;
  pending?: boolean;
  afkWarningMs?: number | null;
  presenceRequired?: boolean;
}) {
  const [section, setSection] = useState<RoomSection>(initialSection);
  if (!view) {
    return (
      <LoadingRoom
        code="XILE"
        mode={mode}
        status={status}
        error={error}
        onRetry={() => undefined}
        onExit={() => undefined}
        onLeave={() => undefined}
        onForgetSession={() => undefined}
      />
    );
  }
  return mode === "tv" ? (
    <TvRoom view={view} status={status} error={error} onRetry={() => undefined} onExit={() => undefined} />
  ) : (
    <PhoneRoom
      view={view}
      status={status}
      section={section}
      setSection={setSection}
      pending={pending}
      actionError={actionError}
      dispatch={async () => undefined}
      error={error}
      onRetry={() => undefined}
      onForgetSession={() => undefined}
      onLeave={() => undefined}
      afkWarningMs={afkWarningMs}
      presenceRequired={presenceRequired}
      confirmPresence={async () => ({ ok: true, revision: view.public.revision })}
    />
  );
}

function LoadingRoom({
  code,
  mode,
  status,
  error,
  onRetry,
  onExit,
  onLeave,
  onForgetSession,
}: {
  code: string;
  mode: "player" | "tv";
  status: ConnectionStatus;
  error: { code: string; message: string } | null;
  onRetry: () => void;
  onExit: () => void;
  onLeave: () => void;
  onForgetSession: () => void;
}) {
  const staleSession = error?.code === "invalid-session";

  return (
    <div className="loading-room">
      <RoomHeader
        code={code}
        status={status}
        tv={mode === "tv"}
        {...(mode === "player" ? { onLeave } : {})}
      />
      <main aria-live="polite">
        {error ? (
          <CircleAlert aria-hidden="true" />
        ) : (
          <LoaderCircle className="spin" aria-hidden="true" />
        )}
        <h1>{staleSession ? "This player session is stale" : error ? "The chamber is unavailable" : "Opening the chamber"}</h1>
        <p>
          {staleSession
            ? "The saved session can no longer resume this room. Clear it and join again; no role or private game data is exposed by this recovery step."
            : error?.message ?? "Restoring the live room record…"}
        </p>
        <div className="loading-room__actions">
          {staleSession ? (
            <button className="button button--primary" type="button" onClick={onForgetSession}>
              <RotateCcw aria-hidden="true" /> Clear session and join again
            </button>
          ) : (
            <button className="button button--primary" type="button" onClick={onRetry}>
              <RotateCcw aria-hidden="true" /> Retry connection
            </button>
          )}
          {staleSession ? (
            <button className="button button--quiet" type="button" onClick={onRetry}>
              Retry saved session
            </button>
          ) : (
            <button className="button button--quiet" type="button" onClick={onExit}>
              <ArrowLeft aria-hidden="true" /> Back to Xile Games
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

function ConnectionNotice({
  status,
  error,
  onRetry,
  onForgetSession,
  tv = false,
}: {
  status: ConnectionStatus;
  error: { code: string; message: string } | null;
  onRetry: () => void;
  onForgetSession?: () => void;
  tv?: boolean;
}) {
  if (status === "live") {
    return null;
  }

  const staleSession = error?.code === "invalid-session";
  const connecting = status === "connecting";

  return (
    <aside
      className={`connection-notice connection-notice--${status}${tv ? " connection-notice--tv" : ""}`}
      role="status"
      aria-live="polite"
    >
      <CircleAlert aria-hidden="true" />
      <div>
        <strong>{staleSession ? "Saved player session needs attention" : connecting ? "Connecting to the chamber" : "Connection to the chamber was lost"}</strong>
        <p>
          {staleSession
            ? "This saved seat can no longer be resumed. Clear the saved session and join again to receive a fresh private seat."
            : error?.message ?? "The current room stays visible, but actions wait until the live connection returns."}
        </p>
        <div className="connection-notice__actions">
          <button className="button button--ink" type="button" onClick={onRetry}>
            <RotateCcw aria-hidden="true" /> Retry connection
          </button>
          {staleSession && !tv && onForgetSession ? (
            <button className="button button--quiet" type="button" onClick={onForgetSession}>
              Clear saved session and join again
            </button>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

function TvRoom({
  view,
  status,
  error = null,
  onRetry,
  onExit,
}: {
  view: RoomView;
  status: ReturnType<typeof useRoomSocket>["status"];
  error?: { code: string; message: string } | null;
  onRetry: () => void;
  onExit: () => void;
}) {
  const room = view.public;
  const audio = useGameAudio(room);
  return (
    <div className={`tv-room tv-room--${room.phase}`}>
      <RoomHeader code={room.code} status={status} tv tvAudio={{ muted: audio.muted, onToggle: audio.toggle }} onTvClose={onExit} />
      <ConnectionNotice status={status} error={error} onRetry={onRetry} tv />
      {room.phase === "lobby" ? (
        <TvLobby room={room} />
      ) : (
        <main className="tv-stage">
          <section className="tv-stage__chamber">
            <PhaseBanner room={room} />
            {room.winner ? <VictoryNotice view={view} /> : null}
            <PublicBoard room={room} />
          </section>
          <PlayerRail room={room} />
        </main>
      )}
      <small className="tv-version-corner">v{APP_VERSION}</small>
    </div>
  );
}

function TvLobby({ room }: { room: RoomView["public"] }) {
  const readyCount = room.players.filter((player) => player.ready).length;
  const connectedCount = room.players.filter((player) => player.connected).length;
  const joinUrl = `${window.location.origin}${window.location.pathname}?room=${room.code}`;
  return (
    <main className="tv-lobby shared-tv-lobby">
      <section className="tv-lobby__intro shared-tv-join">
        <div className="tv-large-qr" aria-label={`QR code to join room ${room.code}`}><QRCodeCanvas value={joinUrl} size={320} bgColor="#f3eadb" fgColor="#17141c" level="M" includeMargin /></div>
        <div className="shared-tv-join-copy"><p className="eyebrow">SCAN TO JOIN</p><h1>Open the chamber.</h1><p>Scan or enter the code on one device per player. Roles and decisions stay private on that device.</p></div>
      </section>
      <aside className="tv-lobby__status shared-tv-status">
        <div className="tv-party-head shared-tv-party-head">
          <p className="eyebrow">PARTY</p>
          <h2>{room.players.length} {room.players.length === 1 ? "player" : "players"}</h2>
          <b>{readyCount}/{room.players.length} ready</b>
        </div>
        <div className="tv-teams shared-tv-list">
          {room.players.length === 0 ? <p>Players join on their own devices.</p> : room.players.map((player) => <div key={player.id}><i /><strong>{player.name}</strong><span>{!player.connected ? "Offline" : player.ready ? "Ready" : "Not ready"}</span></div>)}
        </div>
        <div className="tv-settings shared-tv-settings">
          <span><b>TV</b>Public chamber</span>
          <span><b>PLAYERS</b>{connectedCount} connected</span>
          <span><b>REQUIRED</b>5–10 players</span>
          <span><b>START</b>All ready</span>
        </div>
      </aside>
    </main>
  );
}

function PhoneRoom({
  view,
  status,
  error,
  section,
  setSection,
  pending,
  actionError,
  dispatch,
  onRetry,
  onForgetSession,
  onLeave,
  afkWarningMs,
  presenceRequired,
  confirmPresence,
}: {
  view: RoomView;
  status: ReturnType<typeof useRoomSocket>["status"];
  error?: { code: string; message: string } | null;
  section: RoomSection;
  setSection: (section: RoomSection) => void;
  pending: boolean;
  actionError: string | null;
  dispatch: (command: GameCommand) => Promise<void>;
  onRetry: () => void;
  onForgetSession: () => void;
  onLeave: () => void;
  afkWarningMs: number | null;
  presenceRequired: boolean;
  confirmPresence: () => Promise<import("@secret-cabinet/contracts").GameCommandResult>;
}) {
  const room = view.public;
  const [presenceError, setPresenceError] = useState<string | null>(null);
  const [confirmingPresence, setConfirmingPresence] = useState(false);

  async function handlePresence(): Promise<void> {
    if (status !== "live") {
      onRetry();
      return;
    }
    setConfirmingPresence(true);
    setPresenceError(null);
    const result = await confirmPresence();
    if (!result.ok) setPresenceError(result.message);
    setConfirmingPresence(false);
  }
  return (
    <div className="phone-room">
      <RoomHeader code={room.code} status={status} onLeave={onLeave} />
      <ConnectionNotice
        status={status}
        error={error ?? null}
        onRetry={onRetry}
        onForgetSession={onForgetSession}
      />
      {afkWarningMs !== null && !presenceRequired ? (
        <aside className="afk-warning" role="alert">
          <CircleAlert aria-hidden="true" />
          <div><strong>Are you still at the table?</strong><p>Your current decision needs an answer. Confirm within {Math.max(1, Math.ceil(afkWarningMs / 1_000))} seconds.</p></div>
          <button className="button button--ink" type="button" onClick={() => void handlePresence()} disabled={confirmingPresence}>I’m still here</button>
        </aside>
      ) : null}
      <main className="phone-room__main" id="room-content">
        {section === "chamber" ? (
          <div className="phone-chamber">
            <PhaseBanner room={room} compact />
            {room.winner ? <VictoryNotice view={view} /> : null}
            {view.private ? (
              <PrivateDocket
                room={room}
                player={view.private}
                dispatch={dispatch}
                pending={pending}
                error={actionError}
              />
            ) : (
              <p className="action-error">This device has no private player session.</p>
            )}
            <details className="public-summary">
              <summary>Show the public chamber on this device</summary>
              <PublicBoard room={room} compact />
            </details>
          </div>
        ) : null}
        {section === "players" ? <PlayerRail room={room} compact /> : null}
        {section === "record" ? <HistoryPanel room={room} /> : null}
        {section === "qr" ? <QrPanel code={room.code} /> : null}
        {section === "rules" ? <RulesPanel /> : null}
      </main>
      <RoomNav active={section} onChange={setSection} />
      {presenceRequired ? (
        <div className="presence-layer" role="dialog" aria-modal="true" aria-labelledby="presence-title">
          <section className="presence-card">
            <small>Seat paused</small>
            <h2 id="presence-title">Confirm before returning.</h2>
            <p>Your private seat was marked away because its required decision was inactive. Reconnect explicitly; no role or policy is revealed by this step.</p>
            {presenceError ? <p className="action-error" role="alert">{presenceError}</p> : null}
            <button className="button button--primary" type="button" onClick={() => void handlePresence()} disabled={confirmingPresence}>
              <RotateCcw aria-hidden="true" /> {status === "live" ? confirmingPresence ? "Confirming…" : "Return to my seat" : "Reconnect"}
            </button>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function VictoryNotice({ view }: { view: RoomView }) {
  const winner = view.public.winner;
  if (!winner) {
    return null;
  }
  const reason = {
    "liberal-policies": "Five Liberal policies secured the republic.",
    "fascist-policies": "Six Fascist policies captured the government.",
    "hitler-elected": "Hitler was elected Chancellor after three Fascist policies.",
    "hitler-executed": "Hitler was identified and executed.",
  }[winner.reason];
  return (
    <section className={`victory-notice victory-notice--${winner.party}`} aria-live="assertive">
      <small>Final declaration</small>
      <h2>{capitalise(winner.party)} victory</h2>
      <p>{reason}</p>
    </section>
  );
}

function capitalise(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
